import * as tslib_1 from "tslib";
import removeAttachmentPreviewsForRow from './removeAttachmentPreviewsForRow';
import getDedupedInlinePreviews from '../helpers/getDedupedInlinePreviews';
import { logVerboseUsage } from 'owa-analytics';
import { lazyShouldShowImageView } from 'owa-attachment-model-store';
import { lazyCreateAttachmentFullViewStateAndStoreBackingModel } from 'owa-attachment-well-data';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import { mailStore } from 'owa-mail-store';
import { action, select } from 'satcheljs/lib/legacy';
import { lazyGetAttachment } from 'owa-attachment-model-store';
// tslint:disable-next-line:forbid-import
import { observable } from 'owa-mobx4-adapter';
// CTQ marker when getAttachmentPreviews returned no previews for a conversation
var INLINEPREVIEWS_FALSEPOSITIVE = 'IP_FP';
var INLINEPREVIEWS_INLINEIMAGE_DUP = 'IP_II_DP';
/**
 * Creates and returns the key for a attachment view state for a particular attachment on a row
 * @param rowId  id of the row
 * @param attachmentId id of the attachment
 * @return - key to store the attachmentViewState in the attachmentViewState map
 */
function getRowAttachmentViewStateKey(rowId, attachmentId) {
    return rowId + '_' + attachmentId;
}
export var createAndStoreAttachmentViewState = action('createAndStoreAttachmentViewState')(function createAndStoreAttachmentViewState(rowId, parentItemId, attachment, attachmentPreviewWellView, mailboxInfo) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var attachmentViewStateKey, attachmentViewState, attachmentId;
        var _this = this;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    attachmentViewStateKey = getRowAttachmentViewStateKey(rowId, attachment.AttachmentId.Id);
                    attachmentViewState = listViewStore.attachmentViewStates.get(attachmentViewStateKey);
                    attachmentId = observable.object(tslib_1.__assign({}, attachment.AttachmentId, { mailboxInfo: mailboxInfo }));
                    /**
                     * Because we are calling the lazy function we set the attachmentViewState in the promise.
                     * This guarantees that the order of the attachments is also preserved. If the attachmentViewState is present
                     * in the store and if we directly push it and then push the ones that are not present after they are lazily created
                     * there are chances that we go out of order.
                     */
                    return [4 /*yield*/, lazyCreateAttachmentFullViewStateAndStoreBackingModel
                            .import()
                            .then(function (createAttachmentFullViewStateAndStoreBackingModel) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                            var shouldShowImageView;
                            return tslib_1.__generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        attachmentViewState =
                                            attachmentViewState ||
                                                createAttachmentFullViewStateAndStoreBackingModel(attachmentId, attachment, true /* isReadOnly */, true /* uploadCompleted */, false /* forceStoreBackingModel */, parentItemId);
                                        return [4 /*yield*/, lazyGetAttachment.import()];
                                    case 1:
                                        // Use the attachment model from the store
                                        attachment = (_a.sent())(attachmentId).model;
                                        // Set the attachmentViewState in the store
                                        listViewStore.attachmentViewStates.set(attachmentViewStateKey, attachmentViewState);
                                        return [4 /*yield*/, lazyShouldShowImageView.import()];
                                    case 2:
                                        shouldShowImageView = _a.sent();
                                        if (shouldShowImageView(attachment)) {
                                            attachmentPreviewWellView.imageViewStateIds.push(attachmentViewStateKey);
                                        }
                                        else {
                                            attachmentPreviewWellView.documentViewStateIds.push(attachmentViewStateKey);
                                        }
                                        return [2 /*return*/];
                                }
                            });
                        }); })];
                case 1:
                    /**
                     * Because we are calling the lazy function we set the attachmentViewState in the promise.
                     * This guarantees that the order of the attachments is also preserved. If the attachmentViewState is present
                     * in the store and if we directly push it and then push the ones that are not present after they are lazily created
                     * there are chances that we go out of order.
                     */
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    });
});
/**
 * Process attachment previews for the conversations
 * @param conversations received in the GetAttachmentPreviews response
 * @param mailboxInfo
 */
export function processAttachmentPreviewsForConversations(conversations, mailboxInfo) {
    conversations.forEach(function (conversation) {
        processAttachmentsForConversation(conversation, mailboxInfo);
    });
}
/**
 * Create the attachment well view state for the conversation
 * @param conversation - conversation payload from the getAttachmentPreviews response
 */
export var processAttachmentsForConversation = select({
    listView: function () { return listViewStore; },
})(action('processAttachmentsForConversation')(function processAttachmentsForConversation(conversation, mailboxInfo, state) {
    var conversationItem = state.listView.conversationItems.get(conversation.ConversationId.Id);
    // Conversation may have been removed on the client, in which case we ignore the response
    if (!conversationItem) {
        return;
    }
    if (conversation.AttachmentPreviews) {
        /**
         * Step 1 - De-dupe previews for conversations
         * Most of the times the duplicates that we have seen have same file name but the size is different
         * This happens when the conversation is getting updated in different outlook clients.
         */
        var dedupedPreviews = getDedupedInlinePreviews(conversation.AttachmentPreviews.Previews);
        // Step 2 - process deduped previews for conversation
        processAttachmentsForRow(conversation.ConversationId.Id, dedupedPreviews, mailboxInfo);
        // Log number of inline images that were deduped
        var numOfInlineImagesDeduped = conversation.AttachmentPreviews.Previews.length - dedupedPreviews.length;
        if (numOfInlineImagesDeduped > 0) {
            logVerboseUsage(INLINEPREVIEWS_INLINEIMAGE_DUP, [numOfInlineImagesDeduped]);
        }
    }
    else {
        // Set this flag so that the placeholder space is removed.
        conversationItem.shouldShowAttachmentPreviews = false;
        removeAttachmentPreviewsForRow(conversation.ConversationId.Id);
        // Log everytime we did not receive any attachment previews
        logVerboseUsage(INLINEPREVIEWS_FALSEPOSITIVE);
    }
}));
export var processAttachmentPreviewsForItems = action('processAttachmentPreviewsForItems')(function processAttachmentPreviewsForItems(conversations, itemIds, mailboxInfo) {
    /**
     * Step 1 - Get the conversations for which we have received previews
     * Some shall not return previews as there are false positives.
     */
    var conversationsWithPreviews = conversations.filter(function (conversation) {
        return conversation.AttachmentPreviews;
    });
    /**
     * Step 2 - Create a collection of all the previews received
     */
    var allAttachmentPreviews = [];
    conversationsWithPreviews.forEach(function (conversation) {
        allAttachmentPreviews.push.apply(allAttachmentPreviews, tslib_1.__spread(conversation.AttachmentPreviews.Previews));
    });
    /**
     * Step 3 - Create the final list of previews that should be processed
     * Process only the previews whose parent item id is present in the itemIds list which is
     * the list used to fetch the previews for.
     */
    var previewsToProcess = {};
    allAttachmentPreviews.forEach(function (preview) {
        var itemId = preview.ParentItemId.Id;
        if (itemIds.indexOf(itemId) > -1) {
            if (!previewsToProcess[itemId]) {
                previewsToProcess[itemId] = [];
            }
            previewsToProcess[itemId].push(preview);
        }
    });
    /**
     * Step 4s
     * 1. If the item is already deleted then do not process previews for it. Else
     * 2.1 If preview is not received for item do clean up, set show flag to false and clean up store. else
     * 2.2 Proceed with the processing of the previews
     */
    itemIds.forEach(function (itemId) {
        var clientItem = mailStore.items.get(itemId);
        if (!clientItem) {
            delete previewsToProcess[itemId];
        }
        else {
            if (!previewsToProcess[itemId]) {
                // 1. Set shouldShowAttachmentPreviews flag to false so that the placeholder space is removed.
                clientItem.shouldShowAttachmentPreviews = false;
                // 2. Remove attachment previews from the list view store maps
                removeAttachmentPreviewsForRow(itemId);
            }
            else {
                processAttachmentsForRow(itemId, previewsToProcess[itemId], mailboxInfo);
            }
        }
    });
});
/**
 * Create the attachment well view state for the row
 * @param rowId - row id
 * @param previews - previews to be stored for this row
 * @param mailboxInfo - mailbox info to which the preview belongs
 */
export var processAttachmentsForRow = action('processAttachmentsForRow')(function processAttachmentsForRow(rowId, previews, mailboxInfo) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var attachmentPreviewWellView;
        var _this = this;
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    attachmentPreviewWellView = {
                        documentViewStateIds: [],
                        imageViewStateIds: [],
                    };
                    return [4 /*yield*/, Promise.all(previews.map(function (preview) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                            return tslib_1.__generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, createAndStoreAttachmentViewState(rowId, tslib_1.__assign({}, preview.ParentItemId, { mailboxInfo: mailboxInfo }), preview.Attachment, attachmentPreviewWellView, mailboxInfo)];
                                    case 1:
                                        _a.sent();
                                        return [2 /*return*/];
                                }
                            });
                        }); }))];
                case 1:
                    _a.sent();
                    listViewStore.rowAttachmentPreviewWellViews.set(rowId, attachmentPreviewWellView);
                    return [2 /*return*/];
            }
        });
    });
});
//# sourceMappingURL=processAttachmentsForRow.js.map